import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import storeService from '../services/storeService'; // <-- 1. IMPORT storeService
import { setBusinessHours, clearBusinessHours } from '../services/ota/otaWindow';
import { setOtaEnabled, markOtaPolicyReady, resetOtaPolicy } from '../services/ota/otaSettings';

const StoreContext = createContext(null);
export const useStore = () => useContext(StoreContext);

export const StoreProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [activeStore, setActiveStore] = useState(null);
  const [userStores, setUserStores] = useState([]);
  const [showStoreSelector, setShowStoreSelector] = useState(false);
  const [isInitialAdmin, setIsInitialAdmin] = useState(false); // <-- 2. NEW STATE for setup mode

  // Feed the active store's OTA policy to the updater: whether OTA is enabled
  // for this store, plus its timezone + opening hours so updates only apply
  // outside business hours (when the store is closed). No-op on non-native
  // platforms — the scheduler doesn't run there.
  useEffect(() => {
    const isRealStore = activeStore?._id && activeStore._id !== 'setup';
    // Only trust the policy once the store is fully hydrated. `openingHours` is
    // attached by the store API (never present on the lightweight auth-populated
    // or freshly-selected store), so it's a reliable "hydrated" signal. During
    // the brief un-hydrated window we leave the startup gate closed rather than
    // apply the permissive defaults. `otaUpdatesEnabled` may be absent on legacy
    // store docs (lean read skips schema defaults) — treated as enabled.
    const isHydrated = isRealStore && Array.isArray(activeStore.openingHours);
    if (isHydrated) {
      setOtaEnabled(activeStore.otaUpdatesEnabled);
      setBusinessHours({
        timezone: activeStore.timezone,
        openingHours: activeStore.openingHours,
      });
      markOtaPolicyReady();
    } else {
      clearBusinessHours();
      resetOtaPolicy();
    }
  }, [activeStore]);

  const hydrateStore = async (store) => {
    if (!store?._id) return store;
    const needsHydration =
      store.splitPaymentsEnabled === undefined ||
      store.cashCardQuickPaymentEnabled === undefined ||
      store.splitRefundCashOverrideEnabled === undefined ||
      store.checkoutB2BOrderEnabled === undefined ||
      store.cashDenominationPadEnabled === undefined ||
      store.tillOpeningDirectEntryEnabled === undefined ||
      store.cardPaymentProvider === undefined ||
      // OTA policy fields — decouple OTA correctness from the payment/till
      // select above so the gate can't silently fall back to enabled +
      // unrestricted hours. openingHours is only present on a full fetch.
      store.openingHours === undefined ||
      store.timezone === undefined ||
      store.otaUpdatesEnabled === undefined;
    if (!needsHydration) return store;
    try {
      const fullStore = await storeService.getStoreById(store._id);
      return { ...store, ...fullStore };
    } catch (err) {
      console.warn('Failed to hydrate active store settings', err);
      return store;
    }
  };

  useEffect(() => {
    const checkStoreLogic = async () => {
      if (isAuthenticated && user?.stores) {
        const stores = user.stores;
        setUserStores(stores);

        if (stores.length > 0) {
          // --- NORMAL OPERATION ---
          setIsInitialAdmin(false); // This is not the initial setup
          const lastStoreId = localStorage.getItem('activeStoreId');
          const lastStore = stores.find(s => s._id === lastStoreId);

          if (lastStore) {
            const hydratedStore = await hydrateStore(lastStore);
            setActiveStore(hydratedStore);
            setShowStoreSelector(false);
          } else if (stores.length === 1) {
            const hydratedStore = await hydrateStore(stores[0]);
            setActiveStore(hydratedStore);
            localStorage.setItem('activeStoreId', hydratedStore._id);
            setShowStoreSelector(false);
          } else {
            setShowStoreSelector(true);
          }
        } else if (user.role === 'Admin' || user.roles?.some(r => r.name === 'Admin')) {
          // --- SPECIAL "FIRST RUN" SCENARIO ---
          // User is an admin but has no stores. Let's check if ANY stores exist in the system.
          try {
            const allStores = await storeService.getStores();
            if (allStores.length === 0) {
              // This is the very first admin in a brand new system.
              // Allow them to proceed without a real active store.
              setIsInitialAdmin(true);
              setActiveStore({ name: "Setup Mode", _id: "setup" }); // A temporary placeholder store
              setShowStoreSelector(false);
            } else {
              // Stores exist, but this admin isn't assigned to any. Force selector.
              setShowStoreSelector(true);
            }
          } catch (e) {
            console.error("Could not fetch stores for initial setup check", e);
            setShowStoreSelector(true); // Fail safe and force selector
          }
        } else {
          // Not an admin and no stores assigned. Force selector.
          setShowStoreSelector(true);
        }
      } else if (!isAuthenticated) {
        // Clear everything on logout
        setActiveStore(null);
        setUserStores([]);
        setShowStoreSelector(false);
        setIsInitialAdmin(false);
        localStorage.removeItem('activeStoreId');
      }
    };

    checkStoreLogic();
  }, [user, isAuthenticated]);

  const selectStore = async (store) => {
    setActiveStore(store);
    localStorage.setItem('activeStoreId', store._id);
    localStorage.removeItem('cfdSessionToken');
    setIsInitialAdmin(false); // Selecting a real store always exits setup mode
    setShowStoreSelector(false);
    const hydratedStore = await hydrateStore(store);
    setActiveStore(hydratedStore);
  };

  const switchStore = () => {
    setActiveStore(null);
    localStorage.removeItem('activeStoreId');
    localStorage.removeItem('cfdSessionToken');
    setShowStoreSelector(true);
  };

  const value = {
    activeStore,
    userStores,
    selectStore,
    showStoreSelector,
    switchStore,
    isInitialAdmin, // <-- 3. EXPOSE new state
  };

  return (
    <StoreContext.Provider value={value}>
      {children}
    </StoreContext.Provider>
  );
};
